import { Box, Button, Flex, Icon, Spinner, Text, Textarea, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { HiOutlinePlus, HiOutlineArrowPath, HiOutlineCodeBracket, HiOutlineArrowDownTray, HiOutlineChevronUp } from "react-icons/hi2";
import ScenarioTesterService, { POLL_INTERVAL_MS } from "@/services/agency/ScenarioTesterService";
import IncidentEditor from "@/pages/recruitmentTests/components/IncidentEditor";
import IncidentResultItem from "@/pages/recruitmentTests/components/IncidentResultItem";
import { showToast } from "@/components/ui/toaster";

const createEmptyMessage = () => ({
    input: null,
    output: { exactResponse: null, possibleResponses: [], isPositive: true, expectedEntityId: "", anyOfPhrases: null }
});

const createEmptyIncident = () => ({
    scenarioId: "",
    recruitmentId: "",
    name: "",
    messages: [createEmptyMessage()]
});

const RecruitmentTestsPage = () => {
    const [incidents, setIncidents] = useState([createEmptyIncident()]);
    const [collapsedSet, setCollapsedSet] = useState(new Set());
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [results, setResults] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [jsonPanelOpen, setJsonPanelOpen] = useState(false);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(null);

    useEffect(() => {
        if (!jobId || jobStatus === "done") return;

        const interval = setInterval(() => {
            ScenarioTesterService.getStatus(jobId)
                .then((data) => {
                    setResults(data.results ?? []);
                    if (data.status === "done") {
                        setJobStatus("done");
                        clearInterval(interval);
                    }
                })
                .catch(() => {
                    showToast("Polling error", "Failed to fetch test status", "error");
                });
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [jobId, jobStatus]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const response = await ScenarioTesterService.run(incidents);
            setJobId(response.jobId);
            setJobStatus("running");
            setResults([]);
        } catch {
            showToast("Error", "Failed to start tests", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setJobId(null);
        setJobStatus(null);
        setResults([]);
    };

    const addIncident = () => {
        setIncidents((prev) => [...prev, createEmptyIncident()]);
    };

    const updateIncident = (i, updated) => {
        setIncidents((prev) => prev.map((inc, idx) => (idx === i ? updated : inc)));
    };

    const removeIncident = (i) => {
        setIncidents((prev) => prev.filter((_, idx) => idx !== i));
        setCollapsedSet((prev) => {
            const next = new Set();
            prev.forEach((idx) => { if (idx < i) next.add(idx); else if (idx > i) next.add(idx - 1); });
            return next;
        });
    };

    const toggleCollapsed = (i, altKey) => {
        if (altKey) {
            const allCollapsed = incidents.every((_, idx) => collapsedSet.has(idx));
            setCollapsedSet(allCollapsed ? new Set() : new Set(incidents.map((_, idx) => idx)));
        } else {
            setCollapsedSet((prev) => {
                const next = new Set(prev);
                next.has(i) ? next.delete(i) : next.add(i);
                return next;
            });
        }
    };

    const collapseAll = () => setCollapsedSet(new Set(incidents.map((_, i) => i)));

    const getResultForIncident = (incident) =>
        results.find((r) => r.name === incident.name);

    const handleImportJson = () => {
        setJsonError(null);
        try {
            const parsed = JSON.parse(jsonText);
            if (!Array.isArray(parsed)) {
                setJsonError("JSON musi być tablicą incydentów [ ... ]");
                return;
            }
            setIncidents(parsed);
            setJsonPanelOpen(false);
            setJsonText("");
        } catch (e) {
            setJsonError(`Nieprawidłowy JSON: ${e.message}`);
        }
    };

    const handleExportJson = () => {
        const blob = new Blob([JSON.stringify(incidents, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `incidents-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const toggleJsonPanel = () => {
        setJsonPanelOpen((prev) => !prev);
        setJsonError(null);
    };

    const isRunning = jobStatus === "running";
    const isDone = jobStatus === "done";

    return (
        <VStack w="full" rowGap={6}>
            <Flex flexDirection="row" justifyContent="space-between" alignItems="center" w="full">
                <VStack alignItems="flex-start" gap={0}>
                    <Text color="white" fontSize={24} fontWeight="semibold">Recruitment Tests</Text>
                    {isDone && (
                        <Text color="green.400" fontSize={13}>
                            Completed — {results.filter((r) => r.passed).length}/{results.length} passed
                        </Text>
                    )}
                    {isRunning && (
                        <Text color="text.grey" fontSize={13}>Running tests...</Text>
                    )}
                </VStack>
                <Flex gap={3}>
                    {jobId && (
                        <Button variant="outlined" onClick={handleReset} disabled={isRunning}>
                            <Icon fontSize={18}><HiOutlineArrowPath /></Icon>
                            New Test
                        </Button>
                    )}
                    {!jobId && (
                        <>
                            <Button variant="outlined" onClick={toggleJsonPanel}>
                                <Icon fontSize={18}><HiOutlineCodeBracket /></Icon>
                                {jsonPanelOpen ? "Zamknij JSON" : "Wklej JSON"}
                            </Button>
                            {incidents.length > 0 && (
                                <Button variant="outlined" onClick={handleExportJson}>
                                    <Icon fontSize={18}><HiOutlineArrowDownTray /></Icon>
                                    Eksportuj JSON
                                </Button>
                            )}
                            {incidents.length > 1 && (
                                <Button variant="outlined" onClick={collapseAll}>
                                    <Icon fontSize={18}><HiOutlineChevronUp /></Icon>
                                    Zwiń wszystkie
                                </Button>
                            )}
                            <Button variant="outlined" onClick={addIncident}>
                                <Icon fontSize={18}><HiOutlinePlus /></Icon>
                                Add Incident
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={incidents.length === 0 || isSubmitting}
                            >
                                {isSubmitting ? <Spinner size="sm" /> : "Testuj"}
                            </Button>
                        </>
                    )}
                </Flex>
            </Flex>

            {!jobId && jsonPanelOpen && (
                <Box w="full" bg="bg.secondary" borderRadius={12} p={5} borderWidth="1px" borderColor="border.primary">
                    <Text color="white" fontSize={14} fontWeight="semibold" mb={3}>Import z JSON</Text>
                    <Textarea
                        variant="primary"
                        placeholder='Wklej tablicę incydentów: [ { "scenarioId": "...", ... } ]'
                        value={jsonText}
                        onChange={(e) => { setJsonText(e.target.value); setJsonError(null); }}
                        rows={10}
                        fontFamily="monospace"
                        fontSize={13}
                        mb={3}
                    />
                    {jsonError && (
                        <Text color="red.400" fontSize={13} mb={3}>{jsonError}</Text>
                    )}
                    <Flex gap={3} justifyContent="flex-end">
                        <Button variant="outlined" onClick={toggleJsonPanel}>Anuluj</Button>
                        <Button variant="primary" onClick={handleImportJson} disabled={!jsonText.trim()}>
                            Importuj
                        </Button>
                    </Flex>
                </Box>
            )}

            {!jobId && (
                <VStack w="full" gap={4}>
                    {incidents.map((incident, i) => (
                        <IncidentEditor
                            key={i}
                            index={i}
                            incident={incident}
                            onChange={(updated) => updateIncident(i, updated)}
                            onRemove={() => removeIncident(i)}
                            collapsed={collapsedSet.has(i)}
                            onToggleCollapsed={(e) => toggleCollapsed(i, e.altKey)}
                        />
                    ))}
                    {incidents.length === 0 && (
                        <Text color="text.grey" fontSize={14} fontStyle="italic">
                            Brak incydentów — dodaj przynajmniej jeden
                        </Text>
                    )}
                </VStack>
            )}

            {jobId && (
                <VStack w="full" gap={3}>
                    {incidents.map((incident, i) => (
                        <IncidentResultItem
                            key={i}
                            incident={incident}
                            result={getResultForIncident(incident)}
                        />
                    ))}
                </VStack>
            )}
        </VStack>
    );
};

export default RecruitmentTestsPage;
